import { readAuthSessionClaims } from '../auth-session-claims';
import {
  __resetNativeAuthCallbackUrlsForTests,
  isNativeOAuthCallbackUrl,
  isNativeOAuthCallbackUrlHandled,
  markNativeOAuthCallbackHandled,
  takeClaimedNativeOAuthCallbackFingerprint,
} from './native-auth-callback-url';
import { isNativePlatform } from './platform';
import {
  beginNativePkceVerifierProtection,
  finishNativePkceVerifierProtection,
} from './secure-storage-adapter';
import { __resetNativeAuthSubscriptionReadinessForTests } from './native-auth-subscription-readiness';

import type { Session } from '@supabase/supabase-js';
import type { NativeAuthSessionRead } from './auth-persistence';

const LAUNCH_URL_TIMEOUT_MS = 500;
const LAUNCH_URL_TIMED_OUT = Symbol('launch URL timed out');

export { claimNativeOAuthCallbackUrl, isNativeOAuthCallbackUrl } from './native-auth-callback-url';

export type NativeAuthLaunchDisposition = 'ordinary' | 'callback' | 'unknown';

export type NativeAuthCallbackOutcome =
  | {
      readonly status: 'success';
      readonly session: Session;
      readonly previousIdentity: NativeAuthCallbackPreviousIdentity;
    }
  | { readonly status: 'failure' };

export type NativeAuthCallbackPreviousIdentity =
  | {
      readonly status: 'available';
      readonly userId: string | null;
      readonly sessionId: string | null;
    }
  | { readonly status: 'unavailable' };

const callbackStartListeners = new Set<() => void>();
const callbackOutcomeListeners = new Set<() => void>();

interface NativeAuthCallbackCycle {
  readonly id: number;
  readonly fingerprint: string | null;
  outcomePublished: boolean;
}

interface PendingNativeAuthCallbackOutcome {
  readonly cycleId: number;
  readonly fingerprint: string | null;
  readonly outcome: NativeAuthCallbackOutcome;
}

interface AppliedAuthSessionIdentity {
  readonly accessToken: string;
  readonly sessionId: string | null;
  readonly userId: string;
}

let activeCallbackCycle: NativeAuthCallbackCycle | null = null;
const pendingCallbackOutcomes: PendingNativeAuthCallbackOutcome[] = [];
let nextCallbackCycleId = 0;
let pkceCleanupPending = false;
let pkceCleanupAttempt: Promise<void> | null = null;
let lastAppliedAuthSession: AppliedAuthSessionIdentity | null = null;
let callbackCoordinatorEpoch = 0;

function createCallbackCycle(): NativeAuthCallbackCycle {
  beginNativePkceVerifierProtection();
  pkceCleanupPending = true;
  nextCallbackCycleId += 1;
  return {
    id: nextCallbackCycleId,
    fingerprint: takeClaimedNativeOAuthCallbackFingerprint(),
    outcomePublished: false,
  };
}

function hasPendingCallbackWork(): boolean {
  return activeCallbackCycle !== null || pendingCallbackOutcomes.length > 0;
}

async function waitForCallbackValue<T>(
  getValue: () => T | null,
  listeners: Set<() => void>,
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<T | null> {
  if (signal?.aborted) {
    return null;
  }
  const currentValue = getValue();
  if (currentValue !== null) {
    return currentValue;
  }

  return await new Promise<T | null>((resolve) => {
    let settled = false;
    function finish(value: T | null): void {
      if (settled) {
        return;
      }
      settled = true;
      listeners.delete(onValue);
      globalThis.clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onAbort);
      resolve(value);
    }
    function onValue(): void {
      finish(getValue());
    }
    function onAbort(): void {
      finish(null);
    }
    const timeoutId = globalThis.setTimeout(() => finish(null), Math.max(0, timeoutMs));
    listeners.add(onValue);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function getAppliedAuthSessionIdentity(session: Session): AppliedAuthSessionIdentity {
  return {
    accessToken: session.access_token,
    sessionId: readAuthSessionClaims(session.access_token)?.sessionId ?? null,
    userId: session.user.id,
  };
}

function matchesAppliedAuthSession(session: Session, applied: AppliedAuthSessionIdentity): boolean {
  if (session.user.id !== applied.userId) {
    return false;
  }
  const sessionId = readAuthSessionClaims(session.access_token)?.sessionId ?? null;
  return sessionId && applied.sessionId
    ? sessionId === applied.sessionId
    : session.access_token === applied.accessToken;
}

async function finishPkceProtection(): Promise<void> {
  if (!pkceCleanupPending) {
    return;
  }
  if (pkceCleanupAttempt === null) {
    const attemptEpoch = callbackCoordinatorEpoch;
    const attempt = finishNativePkceVerifierProtection()
      .then(() => {
        if (callbackCoordinatorEpoch === attemptEpoch) {
          pkceCleanupPending = false;
        }
      })
      .finally(() => {
        if (pkceCleanupAttempt === attempt) {
          pkceCleanupAttempt = null;
        }
      });
    pkceCleanupAttempt = attempt;
  }
  await pkceCleanupAttempt;
}

/** Reduce a Keychain read to the identity fields needed for replacement teardown. */
export function getNativeAuthCallbackPreviousIdentity(
  result: NativeAuthSessionRead,
): NativeAuthCallbackPreviousIdentity {
  if (result.status === 'unavailable') {
    return result;
  }
  return {
    status: 'available',
    userId: result.session?.user.id ?? null,
    sessionId: result.session
      ? (readAuthSessionClaims(result.session.access_token)?.sessionId ?? null)
      : null,
  };
}

/**
 * Classify a cold native launch without treating bridge uncertainty as either
 * an ordinary launch or an OAuth callback.
 */
export async function getNativeAuthLaunchDisposition(): Promise<NativeAuthLaunchDisposition> {
  if (!isNativePlatform()) {
    return 'ordinary';
  }

  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  try {
    const launchUrl = await Promise.race([
      import('@capacitor/app').then(async ({ App }) => await App.getLaunchUrl()),
      new Promise<typeof LAUNCH_URL_TIMED_OUT>((resolve) => {
        timeoutId = globalThis.setTimeout(
          () => resolve(LAUNCH_URL_TIMED_OUT),
          LAUNCH_URL_TIMEOUT_MS,
        );
      }),
    ]);
    if (launchUrl === LAUNCH_URL_TIMED_OUT) {
      return 'unknown';
    }
    const url = launchUrl?.url;
    if (typeof url !== 'string' || !isNativeOAuthCallbackUrl(url)) {
      return 'ordinary';
    }
    return isNativeOAuthCallbackUrlHandled(url) ? 'ordinary' : 'callback';
  } catch {
    return 'unknown';
  } finally {
    if (timeoutId !== undefined) {
      globalThis.clearTimeout(timeoutId);
    }
  }
}

/** Arm a fresh verifier-protection cycle, or reject it while prior cleanup is incomplete. */
export function beginNativeAuthCallback(): boolean {
  if (hasPendingCallbackWork()) {
    markNativeOAuthCallbackHandled(takeClaimedNativeOAuthCallbackFingerprint());
    if (pkceCleanupPending) {
      finishPkceProtection().catch(() => null);
    }
    return false;
  }
  if (pkceCleanupPending) {
    markNativeOAuthCallbackHandled(takeClaimedNativeOAuthCallbackFingerprint());
    // A later attempt is also a bounded retry opportunity for a prior failed
    // cleanup, but its authorization code must not be exchanged until cleanup
    // has actually completed.
    finishPkceProtection().catch(() => null);
    return false;
  }

  activeCallbackCycle = createCallbackCycle();
  for (const listener of callbackStartListeners) {
    listener();
  }
  callbackStartListeners.clear();
  return true;
}

/** Wait for a strict callback to be recognized, bounded by the caller's boot deadline. */
export async function waitForNativeAuthCallbackStart(
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<boolean> {
  return (
    (await waitForCallbackValue(
      () => (hasPendingCallbackWork() ? true : null),
      callbackStartListeners,
      timeoutMs,
      signal,
    )) ?? false
  );
}

/** Wait for a leased terminal callback result within the caller's boot deadline. */
export async function waitForNativeAuthCallbackOutcome(
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<NativeAuthCallbackOutcome | null> {
  return await waitForCallbackValue(
    () => pendingCallbackOutcomes[0]?.outcome ?? null,
    callbackOutcomeListeners,
    timeoutMs,
    signal,
  );
}

/** Retire a delivered result only after its identity handoff has committed. */
export function acknowledgeNativeAuthCallbackOutcome(outcome: NativeAuthCallbackOutcome): void {
  const outcomeIndex = pendingCallbackOutcomes.findIndex((pending) => pending.outcome === outcome);
  if (outcomeIndex === -1) {
    return;
  }
  const [acknowledged] = pendingCallbackOutcomes.splice(outcomeIndex, 1);
  markNativeOAuthCallbackHandled(acknowledged?.fingerprint ?? null);
  if (activeCallbackCycle?.id === acknowledged?.cycleId) {
    activeCallbackCycle = null;
  }
}

/** Retire warm-callback results once the ordinary auth subscription applied that exact session. */
export function recordNativeAuthSessionApplied(session: Session | null): void {
  if (!session) {
    lastAppliedAuthSession = null;
    return;
  }
  const applied = getAppliedAuthSessionIdentity(session);
  lastAppliedAuthSession = applied;
  const appliedOutcomes = pendingCallbackOutcomes.filter(
    ({ outcome }) =>
      outcome.status === 'success' && matchesAppliedAuthSession(outcome.session, applied),
  );
  for (const pending of appliedOutcomes) {
    acknowledgeNativeAuthCallbackOutcome(pending.outcome);
  }
}

/** Publish the first terminal result and flush any verifier deletion held during exchange. */
export async function completeNativeAuthCallback(
  outcome: NativeAuthCallbackOutcome,
): Promise<void> {
  activeCallbackCycle ??= createCallbackCycle();
  const cycle = activeCallbackCycle;
  if (!cycle.outcomePublished) {
    cycle.outcomePublished = true;
    pendingCallbackOutcomes.push({
      cycleId: cycle.id,
      fingerprint: cycle.fingerprint,
      outcome,
    });
    for (const listener of callbackOutcomeListeners) {
      listener();
    }
    callbackOutcomeListeners.clear();
    if (
      outcome.status === 'success' &&
      lastAppliedAuthSession &&
      matchesAppliedAuthSession(outcome.session, lastAppliedAuthSession)
    ) {
      acknowledgeNativeAuthCallbackOutcome(outcome);
    }
  }

  await finishPkceProtection();
}

export function __resetNativeAuthLaunchForTests(): void {
  callbackCoordinatorEpoch += 1;
  activeCallbackCycle = null;
  pendingCallbackOutcomes.splice(0);
  nextCallbackCycleId = 0;
  pkceCleanupPending = false;
  pkceCleanupAttempt = null;
  lastAppliedAuthSession = null;
  callbackStartListeners.clear();
  callbackOutcomeListeners.clear();
  __resetNativeAuthCallbackUrlsForTests();
  __resetNativeAuthSubscriptionReadinessForTests();
}
